<template>
  <div class="h5-image-search">
    <div
      v-if="showCanvasImage"
      class="canvas-image-wrapper"
    >
      <div class="image-list-box">
        <div
          v-if="canvasImgList.length"
          class="image-list"
        >
          <div
            class="image-box"
            v-for="(img, index) in canvasImgList"
            :key="index"
          >
            <img
              @click="toggleCanvasImage(index)"
              :src="img.imgUrl"
              alt=""
            >
            <div :class="{active: index === activeIndex}" />
          </div>
        </div>
        <div
          v-else
          class="image-list"
        >
          <div
            class="image-box"
            v-for="(, index) in 3"
            :key="index"
          >
            <div
              class="mockImg"
              :class="{ first: index === 0 }"
            />
            <div :class="{active: index === activeIndex}" />
          </div>
        </div>
      </div>
      <div
        class="close"
        @click="closeImageSearch"
      >
        <img
          src="@/assets/close-circle-icon.svg"
          alt=""
        >
      </div>
    </div>
    <H5Dialog
      class="custom-image-search-dialog"
      :style="getDialogStyle"
      v-model="showImageSearchDlg"
      :scrollable="true"
      :scroll-event="layoutOnScroll"
      :height="`calc(100% - 60px)`"
    >
      <H5Empty v-if="showEmpty" />
      <template v-else>
        <div
          v-if="currentBottomTab === 'similar'"
          class="image-search-title-wrapper">
          <div
            class="image-search-title"
          >
            <div
              class="text1"
            >
              Results for <span>"Similar Recommendations"</span>
            </div>
            <div class="text2">
              All items are sold directly by the manufacturer.
            </div>
          </div>
        </div>
        <!-- ai discovery -->
        <div
          v-else
          class="ai-discovery-wrapper">
          <Discovery 
            ref="discoveryRef"
            @discoverySuccess="handleDiscoverySuccess"
          />
        </div>
        <AiFilters 
          v-if="filterList.length"
          @update="handleAiFiltersUpdate"
          :skeletonLoading="aiFiltersSkeletonLoading"
          :isFixed="aiFiltersFixed"
          :filterList="filterList" />
        <div v-if="currentBottomTab === 'discovery'" class="ai-deep-search-title">Search results for <span>AI Deep Search</span></div>
        <Product module="similar" />

        <!-- 暂时隐藏 -->
        <!-- <div class="bottom-search-bar">
          <div class="search-bar">
            <div
              @click="findSimilar"
              class="find-similar"
              :class="{ active: currentBottomTab === 'similar'}"
            >
              Find Similar
            </div>
            <div
              @click="findStyles"
              class="find-styles"
              :class="{ active: currentBottomTab === 'styles'}"
            >
              Find Styles
            </div>
          </div>
        </div> -->
      </template>
    </H5Dialog>
  </div>
</template>

<script setup>
import H5Dialog from '@/components/H5/H5Dialog/index.vue'
import Product from '@/components/H5/Product/index.vue'
import H5Empty from '@/components/H5/H5Empty/index.vue'
import { onMounted, ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { fetchData } from '@/utils/fetch'
import { buriedPointUtil, gaAnalytics } from '@/utils/utils'
import { useRouter } from 'vue-router'
import Discovery from '@/components/AiDiscovery/components/Discovery.vue'
import AiFilters from './components/AiFilters.vue'

const store = useStore()
const router = useRouter()

const showEmpty = ref(false)
const canvasImgList = ref([]) // 裁剪后的图片列表
const totalNums = ref(0)
const showImageSearchDlg = ref(false)
const showCanvasImage = ref(true)
const activeIndex = ref(0)
const innerHeight = ref(0)
const discoveryRef = ref(null)
const mainImageBase64 = ref('')

const aiFiltersSkeletonLoading = ref(false)
const aiFiltersFixed = ref(false)

// 图搜前五条数据
const topFiveProductList = ref([])

const currencyData = JSON.parse(localStorage.getItem('currencyData'))
const imageSearchInfo = JSON.parse(localStorage.getItem('imageSearchInfo'))

const productList = computed(() => store.state.productList)
const searchInfo = computed(() => store.state.searchInfo)

watch(canvasImgList, (value) => {
  if (value.length) {
    if (imageSearchInfo.type === 'discovery') {
      discoveryRef.value.startDiscovery({
        mainImage: mainImageBase64.value,
        productImage: value[0].imgUrl
      })
    }
  }
})

const observerIdList = ref(new Set())
let observer
onMounted(async () => {
  innerHeight.value = window.innerHeight
  initParams()

  currentBottomTab.value = imageSearchInfo.type
  searchImageData()

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const offerId = entry.target.dataset.id
        observerIdList.value.add(offerId)

        const half = Math.floor(lastPageSize.value / 2)
        const point = productList.value.length - half
        if (observerIdList.value.size >= point) {
          if (currentBottomTab.value === 'discovery') {
            loadMoreAiDiscovery()
          } else {
            loadMore()
          }
        }
      }
    })
  }, { threshold: 0.5 })
})

onUnmounted(() => {
  observer.disconnect()
})

const observeProduct = () => {
  document.querySelectorAll('.product-item').forEach(product => {
    observer.observe(product)
  })
}

const lastPageSize = ref(0)
watch(productList, (value, oldValue) => {
  if (value && value.length) {
    lastPageSize.value = value.length - oldValue.length
    nextTick(() => {
      observeProduct()
    })
  }
})

// 初始化状态
const initParams = () => {
  activeIndex.value = 0
  canvasImgList.value = []
  pageNow.value = 1
}

watch(showImageSearchDlg, (value) => {
  if (value) {
    document.querySelector('body').classList.add('no-scroll')
  } else {
    document.querySelector('body').classList.remove('no-scroll')
  }
})

const setCurrencyData = (array) => {
  const list = array.map(item => {
    return {
      ...item,
      priceInfo: { price: item.priceInfo.price, ...currencyData }
    }
  })
  return list
}

const currentImageRegion = ref()
const searchImageData = async (region = '') => {
  showImageSearchDlg.value = true
  store.commit('setProductList', [])
  store.commit('setLoadMoreSleleton', true)

  currentImageRegion.value = region
  const params = {
    keyword: '',
    imageId: imageSearchInfo.imageId,
    pageNow: 1,
    pageSize: 20,
    region
  }
  if (params.imageId === 0) params.imageAddress = imageSearchInfo.imageUrl
  const { code, data } = await fetchData('/dsp-app/alibaba-common/searchImage', {
    method: 'post',
    body: JSON.stringify(params)
  })
  if (code === 200) {
    gaAnalytics('mp_search_page_view', 'mp_search_page_view')
    const { picRegionInfo, rows, total } = data

    // 空列表处理
    if (rows && !rows.length) {
      store.commit('setEmptyObj', {
        msg: 'No matching results yet',
        showEmpty: true
      })
      store.commit('setLoadMoreSleleton', false)
      showEmpty.value = true
      return
    }

    // 处理图片裁剪
    if (!canvasImgList.value.length) {
      currentImageRegion.value = picRegionInfo.currentRegion
      const coordinates = calculateCropDimensions(picRegionInfo)
      cutImageByCoordinates(imageSearchInfo.imageUrl, coordinates, picRegionInfo).then(list => {
        canvasImgList.value = list
      })
    }

    if (imageSearchInfo.type === 'similar') {
      const list = setCurrencyData(rows || [])
      store.commit('setProductList', list)
      store.commit('setLoadMoreSleleton', false)
      totalNums.value = total
      allowScroll.value = true
    } else {
      // 取图搜前十个商品用于ai discovery
      localStorage.setItem('aiDiscoveryProductList', JSON.stringify(rows.slice(0, 10)))
      // 取图搜前五个商品随机插入视图展示
      topFiveProductList.value = rows.slice(0, 5)
    }
    
  }

  if (code === 401) {
    buriedPointUtil({ eventId: 'MPLG501' })
  }

  if (code === 500) {
    showEmpty.value = true
    store.commit('setLoadMoreSleleton', false)
  }
}

// 根据坐标裁剪图片
const cutImageByCoordinates = async (imgUrl, coordinates, picRegionInfo) => {
  const response = await fetch(imgUrl)
  const blob = await response.blob()
  const base64Image = await blobToBase64(blob)
  mainImageBase64.value = base64Image

  const image = new Image()
  image.src = base64Image
  const list = []

  return new Promise((resolve) => {
    image.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      coordinates.forEach((coord, index) => {
        const width = coord.y0 - coord.x0
        const height = coord.y1 - coord.x1

        canvas.width = width
        canvas.height = height

        ctx.clearRect(0, 0, canvas.width, canvas.height) // 清空画布
        ctx.drawImage(image, coord.x0, coord.x1, width, height, 0, 0, width, height)
        list.push({
          imgUrl: canvas.toDataURL(),
          region: picRegionInfo?.yoloCropRegion?.split(';')[index]
        })
      })
      resolve(list)
    }
  })
}

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const calculateCropDimensions = (picRegionInfo) => {
  const regions = picRegionInfo?.yoloCropRegion?.split(';')
  const result = regions.map((region) => {
    const [x0, y0, x1, y1] = region.split(',').map(Number)
    return { x0, y0, x1, y1 }
  })
  return result
}

const closeImageSearch = () => {
  const productList = JSON.parse(localStorage.getItem('homeProductList'))
  if (productList) {
    store.commit('setProductList', productList)
    localStorage.removeItem('homeProductList')
  }
  store.commit('setSearchInfo', {
    ...searchInfo.value,
    pageNow: 1
  })
  showImageSearchDlg.value = false
  showCanvasImage.value = false
  setTimeout(() => {
    store.commit('h5Module/closeImageSearchDialog')
  }, 300)
  router.go(-1)
}

const allowScroll = ref(true)
const resetScrollPosition = () => {
  document.querySelectorAll('*').forEach((el) => {
    el.scrollTop = 0
  })
  allowScroll.value = false // 禁用滚动
}

const toggleCanvasImage = (index) => {
  buriedPointUtil({ eventId: 'MPSI201' })

  resetScrollPosition()
  activeIndex.value = index
  const region = canvasImgList.value[index].region
  store.commit('setEmptyObj', { msg: '', showEmpty: false })
  pageNow.value = 1

  if (imageSearchInfo.type === 'discovery') {
    store.commit('setProductList', [])
    store.commit('setLoadMoreSleleton', true)

    observerIdList.value.clear()
    filterList.value = []
    discoveryRef.value.cancelCurrentRequests()
    discoveryRef.value.startDiscovery({
      mainImage: mainImageBase64.value,
      productImage: canvasImgList.value[index].imgUrl
    })
  } else {
    searchImageData(region)
  }
}

const layoutOnScroll = (e) => {
  if (!allowScroll.value) return
  const el = e.target
  const scrollTop = el.scrollTop
  const scrollHeight = el.scrollHeight
  const clientHeight = window.innerHeight

  aiFiltersFixed.value = scrollTop > 120

  if (scrollTop + clientHeight >= scrollHeight - 75) {
    if (currentBottomTab.value === 'discovery') {
      loadMoreAiDiscovery()
    } else {
      loadMore()
    }
  }
}

const pageNow = ref(1)
const isSearching = ref(false)
const loadMore = async () => {
  if (isSearching.value) return
  isSearching.value = true

  if (canvasImgList.value.length === 0) return
  allowScroll.value = false

  const params = {
    keyword: '',
    imageId: imageSearchInfo.imageId,
    pageNow: ++pageNow.value,
    pageSize: 20,
    region: currentImageRegion.value
  }
  if (params.imageId === 0) params.imageAddress = imageSearchInfo.imageUrl
  store.commit('setLoadMoreSleleton', true)
  try {
    const { code, data } = await fetchData('/dsp-app/alibaba-common/searchImage', {
      method: 'post',
      body: JSON.stringify(params)
    })
    if (code === 200) {
      buriedPointUtil({ eventId: 'MPSC401', others: { ...params } })
      gaAnalytics('mp_search_page_load_more', 'mp_search_page_load_more')
      const list = setCurrencyData([...productList.value, ...data.rows])
      store.commit('setProductList', list)
      store.commit('setSearchInfo', {
        ...searchInfo.value,
        pageNow: pageNow.value
      })
    }

    if (code === 401) {
      buriedPointUtil({ eventId: 'MPLG501' })
    }

    if (code === 500) {
      showEmpty.value = true
    }

    allowScroll.value = true
    isSearching.value = false
    store.commit('setLoadMoreSleleton', false)
    store.commit('search/SET_PRE_SEARCH', false)
  } catch (error) {
    isSearching.value = false
    store.commit('setLoadMoreSleleton', false)
    store.commit('search/SET_PRE_SEARCH', false)
  }
}

const loadMoreAiDiscovery = async () => {
  if (isSearching.value) return
  isSearching.value = true
  allowScroll.value = false

  const params = {
    keyword: aiDiscoveryKeywords.value + aiProductName.value,
    language: 'zh',
    pageSize: 50,
    pageNow: ++pageNow.value,
    imageId: imageSearchInfo.imageId,
    imageId: 0,
    region: '',
    async: false
  }
  store.commit('setLoadMoreSleleton', true)
  const { code, data } = await fetchData('/dsp-app/alibaba-common/searchKeyWord?itemNumber=50', {
    method: 'post',
    body: JSON.stringify(params)
  })
  if (code === 200) {
    const { rows = [], total } = data
    const list = setCurrencyData([...productList.value, ...rows])
    store.commit('setProductList', list)
    store.commit('setSearchInfo', {
      ...searchInfo.value,
      pageNow: pageNow.value
    })
  }

  allowScroll.value = true
  isSearching.value = false
  store.commit('setLoadMoreSleleton', false)
  store.commit('search/SET_PRE_SEARCH', false)
}

const getDialogStyle = computed(() => {
  if (!imageSearchInfo) return {}
  return {
    background: `linear-gradient(rgba(0, 0, 0, 0.64), rgba(0, 0, 0, 0.64)),
                url(${imageSearchInfo.imageUrl})`,
    'background-size': 'cover',
    'background-position': 'center',
    'background-repeat': 'no-repeat'
  }
})

const currentBottomTab = ref('similar')
const findSimilar = async () => {
  if (currentBottomTab.value === 'similar') return
  buriedPointUtil({ eventId: 'MPOI201' })
  observerIdList.value.clear()

  currentBottomTab.value = 'similar'
  router.replace(router.currentRoute.value.fullPath.replace('discovery', 'similar'))
  resetScrollPosition()

  searchImageData(currentImageRegion.value)
}

const findStyles = async () => {
  if (currentBottomTab.value === 'discovery') return
  buriedPointUtil({ eventId: 'MPOI202' })
  observerIdList.value.clear()

  currentBottomTab.value = 'discovery'
  router.replace(router.currentRoute.value.fullPath.replace('similar', 'discovery'))
  resetScrollPosition()
  searchStylesProductList()
}


const aiDiscoveryKeywords = ref('') // 图搜ai discovery关键词
const aiProductName = ref('') // 图搜ai discovery商品名称
const searchStylesProductList = async () => {
  const keywords = aiDiscoveryKeywords.value + aiProductName.value
  const searchData = {
    keyword: keywords,
    language: 'zh',
    pageSize: 50,
    pageNow: 1,
    imageId: 0,
    region: '',
    async: false
  }
  store.commit('setProductList', [])
  store.commit('setTotal', -1)
  store.commit('setLoadMoreSleleton', true)
  const res = await fetchData('/dsp-app/alibaba-common/searchKeyWord?itemNumber=50', {
    method: 'post',
    body: JSON.stringify(searchData)
  })
  if (res.code === 200) {
    store.commit('setLoadMoreSleleton', false)

    const { rows = [], total } = res.data
    const list = setCurrencyData([...rows])
    
    // 将前五个图搜商品打乱插入到list中
    topFiveProductList.value.forEach(product => {
      const randomIndex = Math.floor(Math.random() * (list.length + 1))
      list.splice(randomIndex, 0, product)
    })

    store.commit('setSearchInfo', searchData)
    store.commit('setProductList', list)
    store.commit('setTotal', total)
    allowScroll.value = true
    isSearching.value = false
    aiFiltersSkeletonLoading.value = false
  }
}

// 处理ai discovery filters
const filterList = ref([])
const handleAiDiscoveryFilters = (res) => {
  aiProductName.value = res.product_main_name.cn
  const filterable_keywords = res.filterable_keywords
  const list = []
  const checkedAttributes = []
  Object.keys(filterable_keywords).forEach(key => {
    const array = filterable_keywords[key]
    const attributes = array.map(item => {
      if (item.default) {
        aiDiscoveryKeywords.value += item.cn
        checkedAttributes.push({
          en: item.en,
          cn: item.cn
        })
      }
      return {
        ...item,
        value: item.en
      }
    })
    list.push({
      isExpand: true,
      showMore: attributes.length < 6,
      title: key,
      attributes,
      checked: attributes.some(item => item.default),
      isExpand: true
    })
  })
  filterList.value = list.reverse();
  store.commit('SET_DISCOVERY_CHECKED_ATTRIBUTES', checkedAttributes)
}

const handleDiscoverySuccess = (res) => {
  handleAiDiscoveryFilters(res)
  searchStylesProductList()
}

const handleAiFiltersUpdate = (keywords) => {
  observerIdList.value.clear()
  aiFiltersSkeletonLoading.value = true
  aiDiscoveryKeywords.value = keywords
  searchStylesProductList()
}

</script>

<style lang="less" scoped>
@import url('./index.less');

.custom-image-search-dialog {
  ::v-deep(.popup-content) {
    background-color: white;
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backdrop-filter: blur(8px);
    z-index: -1;
  }
}

.bottom-search-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 66px;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 80));
  z-index: 9999;
  .search-bar {
    position: relative;
    width: 240px;
    height: 52px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 auto;
    background-color: white;
    border-radius: 26px;
    box-shadow: 0 0 8px #00000016;
    &::before {
      position: absolute;
      content: '';
      width: 0.5px;
      height: 24px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: #E4E6EB;
    }
  }

  .find-similar, .find-styles {
    font-size: 14px;
    color: #b2b7c2;
    font-family: mon-600;
    padding: 7px 0;
    height: 33px;
    -webkit-tap-highlight-color: transparent; /* 透明点击高亮 */

    &.active {
      position: relative;
      color: #232323;
      &::after {
        content: '';
        position: absolute;
        left: 20px;
        right: 20px;
        bottom: 0;
        height: 2px;
        background-color: #232323;
      }
    }
  }
}
</style>
